import React, { useEffect, useRef, useState, forwardRef } from "react";
import mapboxgl from "!mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax
import ReactDOM from "react-dom/client";

import "mapbox-gl/dist/mapbox-gl.css";
import {
  ArrowUturnLeftIcon,
  GlobeAltIcon,
  MapIcon,
} from "@heroicons/react/24/outline";

const Map = forwardRef(
  (
    {
      defaultZoom,
      onLoad,
      onStyleLoad,
      onMapClick,
      onMoveEnd,
      useDarkMode,
      onZoomChange,
    },
    ref
  ) => {
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;
    const mapContainer = useRef(null);
    const lng = -70.9;
    const lat = 42.35;
    const useGlobe = useRef(true);
    const [zoom, setZoom] = useState(defaultZoom ?? 3);
    const handleToggleProjection = () => {
      useGlobe.current = !useGlobe.current;
      // Was immer Sie tun möchten, wenn der Button geklickt wird
      if (useGlobe.current) {
        ref.current.setProjection("globe");
      } else {
        ref.current.setProjection("mercator");
      }
    };
    const handleResetMap = () => {
      ref.current.flyTo({
        center: [lng, lat], // Die ursprünglichen Koordinaten
        zoom: 2, // Der ursprüngliche Zoom-Wert
        bearing: 0, // Setzt die Drehung der Karte zurück
        pitch: 0, // Setzt den Neigungswinkel der Karte zurück
        curve: 1, // Geschwindigkeitskurve des Übergangs
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
      if (ref.current) return; // initialize map only once

      ref.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: useDarkMode
          ? "mapbox://styles/mapbox/dark-v11"
          : "mapbox://styles/mapbox/streets-v12",
        center: [lng, lat],
        zoom: zoom,
      });
      ref.current.on("style.load", () => {
        if (onStyleLoad) onStyleLoad();
      });
      // Schritt 2: Fügen Sie Ihre Steuerung zur Karte hinzu
      const navigationControl = new mapboxgl.NavigationControl({
        visualizePitch: true,
      });

      ref.current.addControl(navigationControl, "top-right");
      ref.current.addControl(
        new ToggleProjectionControl(handleToggleProjection),
        "top-right"
      );
      ref.current.addControl(new ResetMapControl(handleResetMap), "top-right");

      ref.current.on("load", () => {
        ref.current.resize();
        if (onLoad) onLoad();
      });

      ref.current.on("click", (e) => {
        if (onMapClick) onMapClick();
      });
      ref.current.on("zoomend", () => {
        if (onZoomChange) onZoomChange(ref.current.getZoom());
      });

      ref.current.on("moveend", () => {
        if (onMoveEnd) onMoveEnd();
      });
    }, [lng, lat, zoom]);

    return (
      <div className="h-full relative">
        <div ref={mapContainer} className="h-full rounded-lg" />
      </div>
    );
  }
);

export default Map;

class ToggleProjectionControl {
  constructor(onClickCallback) {
    this.onClickCallback = onClickCallback;
    this._container = document.createElement("div");
    this._container.className = "mapboxgl-ctrl mapboxgl-ctrl-group";

    const CustomButton = () => {
      const [localUseGlobe, setLocalUseGlobe] = useState(true);

      const handleLocalClick = () => {
        setLocalUseGlobe(!localUseGlobe);
        this.onClickCallback();
      };

      return (
        <button className="" onClick={handleLocalClick}>
          {localUseGlobe ? (
            <GlobeAltIcon className="mapboxgl-ctrl-icon p-1 " />
          ) : (
            <MapIcon className="mapboxgl-ctrl-icon p-1 " />
          )}
        </button>
      );
    };

    const root = ReactDOM.createRoot(this._container);
    root.render(<CustomButton />);
  }

  handleClick = () => {
    this.useGlobe = !this.useGlobe;

    this.onClickCallback();
  };

  onAdd(map) {
    this._map = map;
    return this._container;
  }

  onRemove() {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    this.map = undefined;
  }
}

class ResetMapControl {
  constructor(onClickCallback) {
    this.onClickCallback = onClickCallback;
    this._container = document.createElement("div");
    this._container.className = "mapboxgl-ctrl mapboxgl-ctrl-group";

    const CustomButton = () => {
      const handleLocalClick = () => {
        this.onClickCallback();
      };

      return (
        <button className="" onClick={handleLocalClick}>
          <ArrowUturnLeftIcon className="mapboxgl-ctrl-icon p-1 " />
        </button>
      );
    };

    const root = ReactDOM.createRoot(this._container);
    root.render(<CustomButton />);
  }

  handleClick = () => {
    this.onClickCallback();
  };

  onAdd(map) {
    this._map = map;
    return this._container;
  }

  onRemove() {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    this.map = undefined;
  }
}
